import { useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import GlobalSearch from './GlobalSearch';
import ClientModeWizard from './clientmode/ClientModeWizard';
import { Menu, Keyboard, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { useClientMode } from '@/lib/clientMode';
import { useKeyboardShortcuts } from '@/components/shared/UXComponents';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { isClientMode, toggleClientMode } = useClientMode();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Global keyboard shortcuts
  useKeyboardShortcuts({
    onSearch: () => {
      const searchInput = document.querySelector('[data-testid="global-search-input"]');
      if (searchInput) searchInput.focus();
    },
    onNewOrder: () => navigate('/app/orders/new'),
    onNewClient: () => navigate('/app/clients/new'),
    onEscape: () => {
      document.activeElement?.blur();
    },
  });

  // CLIENT MODE: Full-screen wizard replaces the entire app
  if (isClientMode) {
    return <ClientModeWizard />;
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--bg)' }}>
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} clientMode={isClientMode} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-14 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b bg-white/80 backdrop-blur-sm sticky top-0 z-30"
          style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="xl:hidden h-9 w-9 p-0"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
            </Button>
            <span className="text-xs font-medium uppercase tracking-wider hidden sm:inline" style={{ color: 'var(--ink-muted)' }}>
              SuiOS
            </span>
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-3">
            {/* Client Mode Toggle */}
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <div 
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-all ${isClientMode ? 'ring-2' : ''}`}
                    style={{ 
                      backgroundColor: isClientMode ? 'rgba(197,165,90,0.15)' : 'rgba(27,42,74,0.04)',
                      ringColor: isClientMode ? 'var(--brand-gold)' : 'transparent',
                      boxShadow: isClientMode ? '0 0 8px rgba(197,165,90,0.4)' : 'none',
                    }}
                    onClick={toggleClientMode}
                    data-testid="client-mode-toggle"
                  >
                    {isClientMode ? (
                      <Eye className="w-3.5 h-3.5" style={{ color: 'var(--brand-gold)' }} />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
                    )}
                    <span className="text-[10px] font-semibold" style={{ color: isClientMode ? 'var(--brand-gold)' : 'var(--ink-muted)' }}>
                      Client Mode
                    </span>
                    <Switch 
                      checked={isClientMode} 
                      onCheckedChange={toggleClientMode}
                      className="h-4 w-7"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <p>{isClientMode ? 'Disable' : 'Enable'} client-facing view (hides internal costs)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Keyboard shortcuts hint */}
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hidden md:flex" aria-label="Keyboard shortcuts">
                    <Keyboard className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <div className="space-y-1">
                    <p><kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">⌘K</kbd> Search</p>
                    <p><kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">⌘N</kbd> New Order</p>
                    <p><kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">⌘⇧C</kbd> New Client</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{user?.name}</p>
              <p className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>{user?.role}</p>
            </div>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
              style={{ backgroundColor: 'rgba(27,42,74,0.08)', color: 'var(--brand-navy)' }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="max-w-[1240px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
